import { exceedsFileSizeLimit } from 'features/shared/utility/fileSizeHelper';
import React, { useState, useRef, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { closeAddAttachment } from 'reducers/PersonDialogsReducer';
import Dialog from 'components/RMSDialog';
import TextField from '@material-ui/core/TextField';
import { uploadRMSAttachment } from 'reducers/helpers/fileHelpers';
import { notify } from 'reducers/NotifierReducer';
import { handleError } from 'reducers/ErrorReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { getAttachmentIcon } from 'features/shared/utility/attachmentIconHelper';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import RMSDictionary from 'components/RMSAutoComplete/RMSDictonary';
import FileUploadHandler from 'components/FileUploadHandler';

const useStyles = makeStyles((theme) => ({
  txtField: {
    minWidth: 600,
  },
  fileInput: {
    display: 'none',
  },
  dropZone: {
    background: theme.colors.grey1,
    height: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 8,
    '& svg': {
      marginRight: 8,
    },
  },
  dropZoneOver: {
    background: theme.colors.grey1,
    height: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `3px dashed ${theme.palette.primary.main}`,
    marginBottom: 8,
    '& svg': {
      marginRight: 8,
    },
  },
  fileInfo: {
    minHeight: 35,
    '& img': {
      maxHeight: 24,
      borderRadius: 3,
      marginRight: 8,
    },
  },
  closeButton: { color: theme.button.color },
  errorText: {
    color: 'red',
    margin: '0px 8px 10px',
    fontSize: '14px',
  },
}));

function AddAttachment(props) {
  const classes = useStyles();
  const { id, type, title = 'Add Attachment' } = props.data;
  const [Description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [draggedOver, setDraggedOver] = useState(false);
  const mountedRef = useRef(true);
  const [codeAttachmentType, setCodeAttachmentType] = useState();
  const attachmentSizeLimit = useSelector((state) => state.attachments.sizelimit);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  const close = () => {
    props.closeAddAttachment();
  };

  const save = async () => {
    props.showSpinner();
    try {
      const newAttachments = [
        {
          parentType: type,
          parentId: id,
          file,
          fileName: file?.name,
          descr: Description,
          codeAttachmentType: codeAttachmentType?.Code ?? null,
        },
      ];
      await uploadRMSAttachment(newAttachments);
      props.notifyDataUpdate({ type: 'file-upload', data: type });
      if (mountedRef.current) setFile(null);
      props.notify('File uploaded', 'success');
      close();
    } catch (err) {
      props.handleError(err);
    }
    props.hideSpinner();
  };

  const renderActions = () => {
    return (
      <>
        <Button
          color="primary"
          variant="contained"
          autoFocus
          onClick={save}
          disabled={!file || !!error}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close} className={classes.closeButton}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const onDrop = (ev) => {
    const files = ev.dataTransfer.files;
    if (!files.length) return setDraggedOver(false);
    setFile(files[0]);
    if (exceedsFileSizeLimit(file, attachmentSizeLimit)) {
      setError(`File size limit exceeded. Maximum allowed per file: ${attachmentSizeLimit} MB.`);
    } else {
      setError('');
    }
    ev.preventDefault();
  };

  const onDragOver = (ev) => {
    if (!draggedOver) setDraggedOver(true);
    ev.preventDefault();
  };

  const onDragLeave = (ev) => {
    if (draggedOver) setDraggedOver(false);
    ev.preventDefault();
  };

  const renderFileInfo = () => {
    const idx = file.name.lastIndexOf('.');
    const type = file.name.substr(idx);
    return (
      <>
        <img src={getAttachmentIcon(type)} alt="" />
        <span>{file.name}</span>
      </>
    );
  };

  const onDescriptionChange = (e) => {
    const text = e.target.value;
    if (text.length > 100) return;
    setDescription(e.target.value);
  };

  const className = draggedOver || Boolean(file) ? classes.dropZoneOver : classes.dropZone;
  return (
    <Dialog toolbar onClose={close} title={title} actions={renderActions()}>
      <div className={className} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
        <FileUploadHandler
          accept="*"
          multiple={false}
          onCapture={(file) => {
            if (!file.name) return; // keep existing dedupe rule
            if (exceedsFileSizeLimit(file, attachmentSizeLimit)) {
              setError(
                `File size limit exceeded. Maximum allowed per file: ${attachmentSizeLimit} MB.`
              );
              return;
            }
            setFile(file);
          }}
        />
      </div>
      <div className={classes.fileInfo}>{Boolean(file) && renderFileInfo()}</div>
      {error && <div className={classes.errorText}>{error}</div>}
      <RMSDictionary
        compact
        options="codeAttachmentTypes"
        value={codeAttachmentType}
        className={classes.item2}
        onChange={(e, value) => setCodeAttachmentType(value)}
        label="Type"
      />
      <TextField
        label="Description"
        variant="outlined"
        size="small"
        value={Description}
        onChange={onDescriptionChange}
        className={classes.txtField}
      />
    </Dialog>
  );
}

export default connect(null, {
  closeAddAttachment,
  notify,
  handleError,
  showSpinner,
  hideSpinner,
  notifyDataUpdate,
})(AddAttachment);
